/**
 * User list — `GET auth/`, role-scoped server-side (`userListScope` in
 * users/routes.ts): SUPERAGENT sees everyone, LANDLORD/AGENT see a real but
 * narrower list (self + their tenants/agents/properties' tenants), TENANT
 * sees only themselves. Mounted at both `/landlord/users` (a plain
 * LANDLORD's own scoped view, or a SUPERAGENT passing through the landlord
 * role tree — see AdminShell's header comment) and `/admin/users`
 * (reachable only by SUPERAGENT) — same component for both, see router.tsx.
 *
 * The create-user form calls the SAME `POST auth/register/` the public
 * signup form uses — there is no separate "admin create user" endpoint. Its
 * role logic is asymmetric (auth/routes.ts) and is reproduced here exactly,
 * not "fixed":
 *   - LANDLORD: 403 unconditionally — no create-user affordance is shown.
 *   - AGENT: may create TENANT only — no role selector, role is hardcoded.
 *   - SUPERAGENT: may create any role — full role selector.
 * The raw `api/endpoints/auth` `register()` is called directly, NOT
 * `AuthContext`'s `register()` (which would log the caller in as the new
 * user) — the returned tokens are discarded, only `user` is used.
 *
 * Role-change / deactivate affordance — NEW, no Django route (see
 * PARITY_NOTES.md): `PATCH`/`DELETE auth/{id}/` are SUPERAGENT-only on the
 * backend, so the row controls below are gated the same way. Deactivation is
 * a soft delete (`is_active: false`); reactivation is the same endpoint with
 * `is_active: true`, so it needs no confirmation the way deactivate does.
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { parseApiError } from '../../lib/apiError';
import { useIsMobile } from '../../lib/useIsMobile';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { deactivateUser, listUsers, register, updateUserAdmin } from '../../api/endpoints/auth';
import { listProperties, listUnits } from '../../api/endpoints/properties';
import { USER_ROLES, fullName, type UserRole } from '../../types/user';

const INITIAL_FORM = {
  first_name: '',
  last_name: '',
  username: '',
  email: '',
  phone: '',
  password: '',
  password2: '',
  role: 'TENANT' as UserRole,
  assigned_property: '',
  assigned_unit: '',
};

type FormState = typeof INITIAL_FORM;

export function UserListPage(): React.JSX.Element {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // views.py:66 / views.py:74-79 — LANDLORD is deliberately excluded from
  // both branches, even though landlords are managers everywhere else.
  const canCreateAnyRole = user?.role === 'SUPERAGENT';
  const canCreateTenantOnly = user?.role === 'AGENT';
  const canCreate = canCreateAnyRole || canCreateTenantOnly;
  const isSuperagent = user?.role === 'SUPERAGENT';
  const isMobile = useIsMobile();

  const [page, setPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  const [confirmDeactivateId, setConfirmDeactivateId] = useState<number | null>(null);

  const query = useQuery({
    queryKey: ['users', { page, roleFilter }],
    queryFn: () => listUsers({ page, role: roleFilter === '' ? undefined : roleFilter }),
  });

  const changeRole = useMutation({
    mutationFn: ({ id, role }: { id: number; role: UserRole }) => updateUserAdmin(id, { role }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const deactivate = useMutation({
    mutationFn: (id: number) => deactivateUser(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const reactivate = useMutation({
    mutationFn: (id: number) => updateUserAdmin(id, { is_active: true }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  // The effective create-role is TENANT either because the (SUPERAGENT-only)
  // role selector is set to it, or because it's hardcoded for an AGENT caller.
  const showPropertyAssignment = (canCreateAnyRole && form.role === 'TENANT') || canCreateTenantOnly;

  const propertiesForAssignment = useQuery({
    queryKey: ['properties', 'for-assignment'],
    queryFn: () => listProperties({ page: 1 }),
    enabled: showPropertyAssignment,
  });

  const unitsForAssignment = useQuery({
    queryKey: ['units', 'for-assignment', form.assigned_property],
    queryFn: () => listUnits({ property: Number(form.assigned_property), is_vacant: true }),
    enabled: showPropertyAssignment && form.assigned_property !== '',
  });

  function update(field: keyof FormState, value: string): void {
    setForm((previous) => ({ ...previous, [field]: value }));
  }

  const create = useMutation({
    mutationFn: () =>
      register({
        email: form.email,
        username: form.username,
        password: form.password,
        password2: form.password2,
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone,
        // Agents may only create TENANT accounts (auth/routes.ts) — forced
        // here regardless of what the (hidden, for an agent) selector holds.
        role: canCreateAnyRole ? form.role : 'TENANT',
        // `property_id` is dead server-side (see api/endpoints/auth.ts's
        // RegisterRequest doc comment) — only `unit_id` actually does
        // anything (creates a PENDING lease), so that's all that's sent.
        unit_id: form.assigned_unit === '' ? undefined : Number(form.assigned_unit),
      }),
    onSuccess: () => {
      setForm(INITIAL_FORM);
      setError(null);
      setFieldErrors({});
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (caught) => {
      const parsed = parseApiError(caught);
      setError(parsed.message);
      setFieldErrors(parsed.fieldErrors);
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    // Checked client-side too: the backend enforces it, but a round trip to
    // be told the two password boxes differ is a poor form experience.
    if (form.password !== form.password2) {
      setError("Password fields didn't match.");
      return;
    }
    create.mutate();
  }

  function fieldError(field: string): string | undefined {
    return fieldErrors[field]?.[0];
  }

  const users = query.data?.results ?? [];
  const hasNext = query.data?.next !== null && query.data?.next !== undefined;
  const hasPrevious = query.data?.previous !== null && query.data?.previous !== undefined;

  const roleCounts = query.data?.role_counts;

  function renderAdminControls(row: (typeof users)[number]): React.JSX.Element {
    if (row.id === user?.id) {
      return <span>— (this is you) —</span>;
    }

    return (
      <span className="admin-controls">
        <select
          aria-label={`Change role for ${fullName(row)}`}
          value={row.role}
          disabled={changeRole.isPending && changeRole.variables?.id === row.id}
          onChange={(event) =>
            changeRole.mutate({ id: row.id, role: event.target.value as UserRole })
          }
        >
          {USER_ROLES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>{' '}
        {row.is_active ? (
          <button
            type="button"
            disabled={deactivate.isPending && deactivate.variables === row.id}
            onClick={() => setConfirmDeactivateId(row.id)}
          >
            Deactivate
          </button>
        ) : (
          <button
            type="button"
            disabled={reactivate.isPending && reactivate.variables === row.id}
            onClick={() => reactivate.mutate(row.id)}
          >
            Reactivate
          </button>
        )}
      </span>
    );
  }

  return (
    <section>
      <h2>Users</h2>

      {roleCounts !== undefined && (
        <ul className="stat-grid">
          {USER_ROLES.map((role) => (
            <li key={role} className="stat-tile">
              <span className="stat-tile__value">{roleCounts[role]}</span>
              <span className="stat-tile__label">{role}</span>
            </li>
          ))}
        </ul>
      )}

      <label htmlFor="user-role-filter">Role</label>
      <select
        id="user-role-filter"
        value={roleFilter}
        onChange={(event) => {
          setRoleFilter(event.target.value as UserRole | '');
          setPage(1);
        }}
      >
        <option value="">All</option>
        {USER_ROLES.map((value) => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </select>

      {query.isPending && <p>Loading…</p>}
      {query.isError && (
        <p role="alert" className="form__error">
          {parseApiError(query.error).message}
        </p>
      )}
      {query.isSuccess && users.length === 0 && <EmptyState icon={Users} message="No users found." />}

      {query.isSuccess && users.length > 0 && (
        isMobile ? (
          <ul className="record-cards">
            {users.map((row) => (
              <li key={row.id} className="record-card">
                <div className="record-card__row">
                  <span className="record-card__label">Name</span>
                  <span className="record-card__value">{fullName(row)}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Email</span>
                  <span className="record-card__value">{row.email}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Role</span>
                  <span className="record-card__value">{row.role}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Property</span>
                  <span className="record-card__value">{row.property_name ?? '—'}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Unit</span>
                  <span className="record-card__value">{row.unit_number ?? '—'}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Lease status</span>
                  <span className="record-card__value">{row.lease_status}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Balance</span>
                  <span className="record-card__value">{row.current_balance}</span>
                </div>
                {isSuperagent && (
                  <div className="record-card__row">
                    <span className="record-card__label">Admin</span>
                    <span className="record-card__value">
                      {renderAdminControls(row)}
                    </span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Property</th>
                <th>Unit</th>
                <th>Lease status</th>
                <th>Balance</th>
                {isSuperagent && <th>Admin</th>}
              </tr>
            </thead>
            <tbody>
              {users.map((row) => (
                <tr key={row.id}>
                  <td>{fullName(row)}</td>
                  <td>{row.email}</td>
                  <td>{row.role}</td>
                  <td>{row.property_name ?? '—'}</td>
                  <td>{row.unit_number ?? '—'}</td>
                  <td>{row.lease_status}</td>
                  <td>{row.current_balance}</td>
                  {isSuperagent && <td>{renderAdminControls(row)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      <p>
        <button type="button" disabled={!hasPrevious} onClick={() => setPage((p) => p - 1)}>
          Previous
        </button>{' '}
        <span>Page {page}</span>{' '}
        <button type="button" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
          Next
        </button>
      </p>

      {canCreate && (
        <>
          <h3>Add user</h3>
          <form onSubmit={handleSubmit} noValidate>
            {error !== null && (
              <p role="alert" className="form__error">
                {error}
              </p>
            )}

            <label htmlFor="user-first-name">First name</label>
            <input
              id="user-first-name"
              required
              value={form.first_name}
              onChange={(event) => update('first_name', event.target.value)}
            />

            <label htmlFor="user-last-name">Last name</label>
            <input
              id="user-last-name"
              required
              value={form.last_name}
              onChange={(event) => update('last_name', event.target.value)}
            />

            <label htmlFor="user-username">Username</label>
            <input
              id="user-username"
              autoComplete="username"
              required
              value={form.username}
              onChange={(event) => update('username', event.target.value)}
            />
            {fieldError('username') !== undefined && (
              <p className="form__field-error">{fieldError('username')}</p>
            )}

            <label htmlFor="user-email">Email</label>
            <input
              id="user-email"
              type="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={(event) => update('email', event.target.value)}
            />
            {fieldError('email') !== undefined && (
              <p className="form__field-error">{fieldError('email')}</p>
            )}

            <label htmlFor="user-phone">Phone (optional)</label>
            <input
              id="user-phone"
              type="tel"
              value={form.phone}
              onChange={(event) => update('phone', event.target.value)}
            />

            {/* Backend rule: at least 8 characters and not entirely numeric
                (refactor/backend/src/lib/passwordValidation.ts). */}
            <label htmlFor="user-password">Password</label>
            <input
              id="user-password"
              type="password"
              autoComplete="new-password"
              required
              value={form.password}
              onChange={(event) => update('password', event.target.value)}
            />
            {fieldError('password') !== undefined && (
              <p className="form__field-error">{fieldError('password')}</p>
            )}

            <label htmlFor="user-password2">Confirm password</label>
            <input
              id="user-password2"
              type="password"
              autoComplete="new-password"
              required
              value={form.password2}
              onChange={(event) => update('password2', event.target.value)}
            />

            {canCreateAnyRole && (
              <>
                <label htmlFor="user-role">Account role</label>
                <select
                  id="user-role"
                  value={form.role}
                  onChange={(event) => update('role', event.target.value)}
                >
                  {USER_ROLES.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </>
            )}

            {showPropertyAssignment && (
              <>
                <label htmlFor="user-assigned-property">Assigned property</label>
                <select
                  id="user-assigned-property"
                  value={form.assigned_property}
                  onChange={(event) => {
                    // Units are property-specific — changing the property
                    // invalidates whatever unit was previously selected.
                    setForm((previous) => ({
                      ...previous,
                      assigned_property: event.target.value,
                      assigned_unit: '',
                    }));
                  }}
                >
                  <option value="">— None —</option>
                  {(propertiesForAssignment.data?.results ?? []).map((property) => (
                    <option key={property.id} value={property.id}>
                      {property.name}
                    </option>
                  ))}
                </select>

                <label htmlFor="user-assigned-unit">Assigned unit</label>
                <select
                  id="user-assigned-unit"
                  value={form.assigned_unit}
                  disabled={form.assigned_property === ''}
                  onChange={(event) => update('assigned_unit', event.target.value)}
                >
                  <option value="">— None —</option>
                  {(unitsForAssignment.data?.results ?? []).map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.unit_number}
                    </option>
                  ))}
                </select>
              </>
            )}

            <button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Add user'}
            </button>
            {create.isSuccess && <span role="status"> User created.</span>}
          </form>
        </>
      )}

      <ConfirmDialog
        open={confirmDeactivateId !== null}
        title="Deactivate user"
        message="Deactivate this user? They will no longer be able to log in. This can be undone by reactivating them."
        tone="danger"
        confirmLabel="Deactivate"
        onConfirm={() => {
          if (confirmDeactivateId !== null) deactivate.mutate(confirmDeactivateId);
          setConfirmDeactivateId(null);
        }}
        onCancel={() => setConfirmDeactivateId(null)}
      />
    </section>
  );
}

export default UserListPage;
